// @adr SPARK-0055-document-displays-use-one-public-boundary@2: The Power PDF generator keeps its isolated renderer; existing file previews use DocumentView.
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { Modal } from "@/components/design/Modal";
import { Switch } from "@/components/design/Switch";
import {
  buildPowerProfilePdfPayload,
  DEFAULT_POWER_PROFILE_PDF_SETTINGS,
  POWER_PROFILE_PDF_SOURCES,
  selectedSources,
  type PowerProfilePdfSettings,
} from "@/lib/power-profile-pdf-model";
import {
  isPowerProfileRendererResponse,
  powerProfileRendererDocumentUrl,
  type PowerProfileRendererRequest,
} from "@/lib/power-profile-renderer-protocol";
import { POWER_SCORE_LENS_LABEL_IDS, type LevelMap, type ScoreSource } from "@/lib/score-source";

const STORAGE_KEY = "spark.powerProfilePdf.settings";

const SOURCE_LABEL_IDS: Record<ScoreSource, string> = {
  self: POWER_SCORE_LENS_LABEL_IDS.SELF_ASSESSMENT,
  smart: POWER_SCORE_LENS_LABEL_IDS.SMART_ANALYSIS,
  combined: POWER_SCORE_LENS_LABEL_IDS.COMBINED,
};

function readStoredSettings(): PowerProfilePdfSettings {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_POWER_PROFILE_PDF_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<PowerProfilePdfSettings> | null;
    const sources = { ...DEFAULT_POWER_PROFILE_PDF_SETTINGS.sources };
    for (const source of POWER_PROFILE_PDF_SOURCES) {
      const stored = parsed?.sources?.[source];
      if (typeof stored === "boolean") sources[source] = stored;
    }
    return {
      // A stored state with nothing ticked would print an empty document; the defaults win.
      sources: selectedSources(sources).length > 0 ? sources : DEFAULT_POWER_PROFILE_PDF_SETTINGS.sources,
      details: typeof parsed?.details === "boolean" ? parsed.details : DEFAULT_POWER_PROFILE_PDF_SETTINGS.details,
    };
  } catch {
    return DEFAULT_POWER_PROFILE_PDF_SETTINGS;
  }
}

function storeSettings(settings: PowerProfilePdfSettings): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Export settings are convenience-only; blocked storage must not break the export.
  }
}

// The caller mounts this dialog only while it is open, so every open starts with a fresh iframe, a
// fresh ready handshake and the export date of that moment.
export function PowerProfilePdfExportModal({
  onClose,
  levels,
  personName,
}: {
  onClose: () => void;
  // Level maps keyed by power abbreviation, loaded by the caller; null while they load, so the
  // dialog opens at once and an empty document can never be downloaded.
  levels: Record<ScoreSource, LevelMap> | null;
  personName: string;
}) {
  const intl = useIntl();
  const [settings, setSettings] = useState<PowerProfilePdfSettings>(readStoredSettings);
  const [rendererReady, setRendererReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rendererFrame = useRef<HTMLIFrameElement | null>(null);
  const tickedCount = selectedSources(settings.sources).length;

  const payload = useMemo(
    () =>
      levels &&
      buildPowerProfilePdfPayload({
        levels,
        settings,
        fileNamePrefix: intl.formatMessage({ id: "grow.powerProfilePdf.fileNamePrefix", defaultMessage: "Power-Profile" }),
        personName,
        locale: intl.locale,
        now: new Date(),
      }),
    [levels, settings, personName, intl],
  );

  function postToRenderer(message: PowerProfileRendererRequest) {
    rendererFrame.current?.contentWindow?.postMessage(message, window.location.origin);
  }

  useEffect(() => {
    storeSettings(settings);
  }, [settings]);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      if (event.source !== rendererFrame.current?.contentWindow) return;
      if (!isPowerProfileRendererResponse(event.data)) return;
      if (event.data.type === "power-profile-renderer-ready") {
        setRendererReady(true);
      } else {
        setError(intl.formatMessage({ id: "grow.powerProfilePdf.downloadFailed", defaultMessage: "Download failed." }));
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [intl]);

  useEffect(() => {
    if (!rendererReady || !payload) return;
    const timer = setTimeout(() => postToRenderer({ type: "power-profile-render", payload }), 300);
    return () => clearTimeout(timer);
  }, [payload, rendererReady]);

  function toggleSource(source: ScoreSource) {
    setSettings((prev) => ({ ...prev, sources: { ...prev.sources, [source]: !prev.sources[source] } }));
  }

  function download() {
    if (!payload) return;
    setError(null);
    postToRenderer({ type: "power-profile-download", payload });
  }

  return (
    <Modal
      open
      onClose={onClose}
      dismiss="light"
      large
      fillViewport
      title={<FormattedMessage id="grow.powerProfilePdf.exportTitle" defaultMessage="Export Power Profile" />}
      footer={
        <Button variant="primary" onClick={download} disabled={!rendererReady || !payload}>
          <FormattedMessage id="grow.powerProfilePdf.download" defaultMessage="Download PDF" />
        </Button>
      }
    >
      <div className="grid min-h-0 flex-1 grid-cols-[17rem_minmax(0,1fr)] gap-4">
        <aside className="min-h-0 overflow-auto border-r border-ink/10 pr-4">
          {error ? <div className="box-flat mb-4 bg-ink/[0.06] p-2 text-smycles-meta text-ink">{error}</div> : null}
          <div className="smycles-field-label mb-2">
            <FormattedMessage id="grow.powerProfilePdf.sources" defaultMessage="Sources" />
          </div>
          <div className="space-y-2">
            {POWER_PROFILE_PDF_SOURCES.map((source) => {
              const checked = settings.sources[source];
              return (
                <label key={source} className="smycles-field-value flex cursor-pointer items-center gap-2 text-ink">
                  <input
                    type="checkbox"
                    checked={checked}
                    // The last ticked source stays ticked: a document without scores says nothing.
                    disabled={checked && tickedCount === 1}
                    onChange={() => toggleSource(source)}
                    className="h-4 w-4 shrink-0 accent-ink"
                  />
                  <span>{intl.formatMessage({ id: SOURCE_LABEL_IDS[source] })}</span>
                </label>
              );
            })}
          </div>
          <div className="smycles-field-label mb-2 mt-6">
            <FormattedMessage id="grow.powerProfilePdf.sections" defaultMessage="Sections" />
          </div>
          <div className="smycles-field-value flex items-center justify-between gap-3 text-ink">
            <span>
              <FormattedMessage id="grow.powerProfilePdf.details" defaultMessage="Power details" />
            </span>
            <Switch
              checked={settings.details}
              onChange={() => setSettings((prev) => ({ ...prev, details: !prev.details }))}
              ariaLabel={intl.formatMessage({ id: "grow.powerProfilePdf.details", defaultMessage: "Power details" })}
            />
          </div>
        </aside>
        <div className="min-h-0 bg-ink/[0.06]">
          <iframe
            id="power-profile-pdf-preview"
            ref={rendererFrame}
            src={powerProfileRendererDocumentUrl()}
            title={intl.formatMessage({
              id: "grow.powerProfilePdf.previewTitle",
              defaultMessage: "Power Profile PDF preview",
            })}
            className="h-full w-full border-0"
          />
        </div>
      </div>
    </Modal>
  );
}
